
class AuthService {
  constructor() {
    this.API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    this.TOKEN_KEY = 'token';
    this.USER_KEY = 'user';
  }

  async signup(userData) {
    try {
      const response = await fetch(`${this.API_URL}/auth/signup`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: userData.name,
          contact: userData.contact,
          password: userData.password,
          confirmPassword: userData.confirmPassword,
          role: userData.role || 'employee' // Default to employee
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        this.setSession(data);
      }
      
      return data;
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    }
  }

  async login(credentials) {
    try {
      const response = await fetch(`${this.API_URL}/auth/login`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(credentials)
      });
      
      const data = await response.json();
      
      if (data.success) {
        this.setSession(data);
      }
      
      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  async getCurrentUser() {
    try {
      const token = this.getToken();
      if (!token) return null;

      const response = await fetch(`${this.API_URL}/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        this.updateUser(data.user);
        return data.user;
      }
      
      return null;
    } catch (error) {
      console.error('Get current user error:', error);
      return null;
    }
  }

  async updateProfile(userData) {
    try {
      const token = this.getToken();
      const response = await fetch(`${this.API_URL}/auth/update`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        this.updateUser(data.user);
      }
      
      return data;
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  }

  async changePassword(passwordData) {
    try {
      const token = this.getToken();
      const response = await fetch(`${this.API_URL}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(passwordData)
      });
      
      return await response.json();
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    }
  }

  async logout() {
    // Perform logout request but handle network errors gracefully.
    const token = this.getToken();
    if (!token) {
      this.clearSession();
      return { success: true, message: 'No token present' };
    }

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      // If offline, just clear session and return
      this.clearSession();
      return { success: false, message: 'Offline: could not contact server' };
    }

    try {
      const resp = await fetch(`${this.API_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!resp.ok) {
        const text = await resp.text().catch(() => null);
        this.clearSession();
        return { success: false, message: `Server responded with ${resp.status} ${resp.statusText}`, details: text };
      }

      this.clearSession();
      return { success: true };
    } catch (error) {
      // Network or CORS error
      console.error('Logout error:', error);
      this.clearSession();
      return { success: false, message: error.message || 'Network error' };
    }
  }

  setSession(authResult) {
    if (authResult.token) {
      localStorage.setItem(this.TOKEN_KEY, authResult.token);
    }
    if (authResult.user) {
      localStorage.setItem(this.USER_KEY, JSON.stringify(authResult.user));
    }
  }

  clearSession() {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
  }

  getToken() {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  getUser() {
    const user = localStorage.getItem(this.USER_KEY);
    try {
      return user ? JSON.parse(user) : null;
    } catch {
      return null;
    }
  }

  updateUser(userData) {
    const currentUser = this.getUser();
    if (currentUser) {
      const updatedUser = { ...currentUser, ...userData };
      localStorage.setItem(this.USER_KEY, JSON.stringify(updatedUser));
    } else {
      localStorage.setItem(this.USER_KEY, JSON.stringify(userData));
    }
  }

  isAuthenticated() {
    const token = this.getToken();
    if (!token) return false;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expired = payload.exp < Date.now() / 1000;
      if (expired) {
        this.clearSession();
        return false;
      }
      return true;
    } catch {
      return true;
    }
  }

  getUserRole() {
    const user = this.getUser();
    return user ? user.role : null;
  }

  isAdmin() {
    const role = this.getUserRole();
    return role === 'admin' || role === 'superadmin';
  }

  isSuperAdmin() {
    return this.getUserRole() === 'superadmin';
  }

  isEmployee() {
    return this.getUserRole() === 'employee';
  }
}

// Create and export a single instance
const authService = new AuthService();
export default authService;